// Building the device the bridge is asked to add.
//
// Extracted from use-bridge-add-device so it can be asserted without a bridge,
// a room, or an auth token. This is the WIRING the control fix depends on:
// model-selection.ts decides what the control settings should be, and this
// decides whether they reach the candidate at all. A correct decision that
// never arrives is the same outage as a wrong one.
//
// `import type` only — the device barrel's runtime graph reaches a Vite
// `?worker` module jest cannot execute, so a value import here would drag it
// into every suite that touches onboarding.
//
// Per rules: ai-code-requires-tests, code-comments-required,
// reference-rules-in-comments (ai.knowledge/global).

import { Protocol as VideoProtocol } from '@studio/models/video';
import type { Agent } from '@studio/models/agent';
import type { Device, Model } from '@studio/models/device';
import type { Minimist } from '#~/device/index.js';
import type { CameraConfig } from './wizard/types.js';

export interface CameraCandidateInput {
  readonly id: Device['id'];
  readonly agentId: Agent['id'];
  readonly config: CameraConfig;
  /**
   * Control settings from the chosen model's registry template. Undefined when
   * the user chose "No Control" or never identified the camera.
   */
  readonly control?: Minimist['control'] | undefined;
  /** The chosen model, so the bridge resolves the same template we did. */
  readonly modelId?: Model['id'] | undefined;
  /**
   * A video configuration the CAMERA reported, or one the model's registry
   * template supplied. Wins over anything composed from the form.
   *
   * An NDI source is addressed by name and an ONVIF camera serves a path
   * nothing can guess (`media/video1` on the Bolin N2-220x), so composing
   * `rtsp://host` from the form made both unaddable. Credentials are still
   * layered on here, because a discovery never carries them.
   */
  readonly video?: unknown;
}

/**
 * The Minimist candidate for a camera the user is adding.
 *
 * Three fields are conditional, and each omission is load-bearing:
 *
 * - `modelId` — without it the bridge builds the device against a DEFAULT
 *   model, which is how a PTZOptics ended up being controlled on port 52381.
 * - `control` — omitted entirely rather than defaulted, so the connection test
 *   hides its control row instead of failing a check against an address nobody
 *   has reason to believe in.
 * - `credentials` — omitted when no username was given, so an empty pair is
 *   never sent as if it were a login.
 *
 * `video` is composed from the form ONLY as a last resort. When the camera or
 * its registry template reported an address, that address is used as-is.
 */
export const buildCameraCandidate = (input: CameraCandidateInput): Minimist => {
  const { id, agentId, config, control, modelId, video } = input;

  const credentials =
    config.username !== ''
      ? { credentials: { username: config.username, password: config.password } }
      : {};

  return {
    id,
    name: config.name || 'Camera',
    agentId,
    ...(modelId === undefined ? {} : { modelId }),
    video:
      video === undefined
        ? {
            protocol: VideoProtocol.RTSP,
            host: config.host,
            port: config.rtspPort,
            path: config.rtspPath,
            ...credentials
          }
        : { ...video, ...credentials },
    ...(control === undefined ? {} : { control })
  };
};
